import EventEmitter from 'eventemitter3'
import throttle from 'lodash/throttle'
import type Delta from 'quill-delta-enhanced'
import type { ICanvasContext, ISearchResult, EnumListType } from '@fast-editor/core'
import {
  SelectionServiceEvent,
  HistoryStackServiceEvent,
  SearchServiceEvent,
  DocumentEvent,
  Document,
  SelectionService,
  TableService,
  SearchService,
  ContentService,
  QuoteBlockService,
  CodeBlockService,
  ParagraphService,
  ListService,
  isPointInRectangle,
  HistoryStackService,
  getPlatform,
  compareDocPos,
  initPlatform,
  bind,
  mapRunToFragment,
  Paragraph,
  ListItem,
  QuoteBlock,
  Table,
  CodeBlock,
  FragmentText,
  FragmentParaEnd,
  FragmentDate,
  FragmentImage,
  LayoutFrame,
  RunText,
  RunParaEnd,
  RunDate,
} from '@fast-editor/core'

import ContentController from './Controller/ContentController'
import TableController from './Controller/TableController'
import type { IEditorConfig } from './IEditorConfig'
import { ToolbarController } from './Controller/ToolbarController/Toolbar'
import { DEFAULT_TOOLBAR_CONFIG } from './Controller/ToolbarController/IToolbarConfig'

import { platform as browserPlatform } from './Platform.browser'

import './styles/editor.scss'
import { RunImageWeb } from './RenderStructure/RunImageWeb'
import { WebCanvasContext } from './WebCanvasContext'

initPlatform(browserPlatform)

// 在 IoC 中注册 各种 Block
bind(Paragraph.typeName, Paragraph)
bind(ListItem.typeName, ListItem)
bind(QuoteBlock.typeName, QuoteBlock)
bind(Table.typeName, Table)
bind(CodeBlock.typeName, CodeBlock)
// 在 IoC 中注册 各种 Fragment
bind(FragmentText.typeName, FragmentText)
bind(FragmentParaEnd.typeName, FragmentParaEnd)
bind(FragmentDate.typeName, FragmentDate)
bind(FragmentImage.typeName, FragmentImage)
// 在 IoC 中注册 LayoutFrame
bind(LayoutFrame.typeName, LayoutFrame)
// 在 IoC 中注册各种 Run
bind(RunText.typeName, RunText)
bind(RunParaEnd.typeName, RunParaEnd)
bind(RunDate.typeName, RunDate)
bind(RunImageWeb.typeName, RunImageWeb)

mapRunToFragment(FragmentText.typeName, RunText.typeName)
mapRunToFragment(FragmentParaEnd.typeName, RunParaEnd.typeName)
mapRunToFragment(FragmentDate.typeName, RunDate.typeName)
mapRunToFragment(FragmentImage.typeName, RunImageWeb.typeName)

export enum EditorEvent {
  EDITOR_CHANGE_SIZE = 'EDITOR_CHANGE_SIZE',
  DOCUMENT_FIRST_RENDERED = 'DOCUMENT_FIRST_RENDERED',
  DOCUMENT_FIRST_LAYOUT = 'DOCUMENT_FIRST_LAYOUT',
}

/**
 * 重绘类型
 */
enum RenderType {
  NoRender = 0b00, // 不需要重绘
  FastRender = 0b01, // 快速重绘
  Render = 0b10, // 重拍并重绘
}

/**
 * 编辑器类
 */
export class Editor extends EventEmitter {
  public config: IEditorConfig
  public scrollTop = 0

  public cvsOffsetX = 0
  // the most outer container of editor
  public container: HTMLDivElement
  // the container of toolbar
  public toolbarContainer: HTMLDivElement
  // the container of document content
  public docContentContainer: HTMLDivElement
  public cvsDoc: HTMLCanvasElement = document.createElement('canvas')
  public cvsCover: HTMLCanvasElement = document.createElement('canvas')
  public domElementContainerWrapper: HTMLDivElement = document.createElement('div')
  public domElementContainer: HTMLDivElement = document.createElement('div')
  public docContentHeight = 0
  /**
   * 编辑器画布 context 对象
   */
  public ctx: ICanvasContext = new WebCanvasContext(
    this.cvsDoc.getContext('2d') as CanvasRenderingContext2D,
    this.cvsCover.getContext('2d') as CanvasRenderingContext2D,
    this.domElementContainer,
  )

  private heightPlaceholderContainer: HTMLDivElement = document.createElement('div')
  private heightPlaceholder: HTMLDivElement = document.createElement('div')
  private divCursor: HTMLDivElement = document.createElement('div')
  private textInput: HTMLTextAreaElement = document.createElement('textarea')
  private composing = false // 输入法输入过程中，CompositionStart 将这个变量标记为 true， CompositionEnd 将这个变量标记为 false
  /**
   * 编辑器画布 DOM 元素
   */
  private doc: Document = new Document()
  private needRender: RenderType = RenderType.NoRender

  // 标记鼠标指针是否在文档区域内
  private isPointerHoverDoc = false
  // 记录当前鼠标在文档的哪个位置
  private currentPointerScreenX = 0
  private currentPointerScreenY = 0

  private selectionService: SelectionService
  private searchService: SearchService
  private contentService: ContentService
  private historyStackService: HistoryStackService
  private tableService: TableService
  private tableController: TableController
  private paragraphService: ParagraphService
  private quoteBlockService: QuoteBlockService
  private codeBlockService: CodeBlockService
  private listService: ListService
  private contentController: ContentController
  private toolbarController: ToolbarController

  // 即将插入的内容的格式

  private setEditorHeight = throttle((newSize: { height: number }) => {
    this.heightPlaceholder.style.height = `${newSize.height}px`
    this.emit(EditorEvent.EDITOR_CHANGE_SIZE, newSize)
  }, 100)

  private changeCursorStatus = (() => {
    let cursorVisible = false
    let blinkTimer: number
    const setCursorVisibility = (visibility: boolean) => {
      this.divCursor.style.opacity = visibility === true ? '1' : '0'
      cursorVisible = visibility
    }
    return (status: { visible?: boolean; color?: string; x?: number; y?: number; height?: number }) => {
      if (status.color !== undefined) {
        this.divCursor.style.borderLeftColor = status.color
      }
      if (status.x !== undefined) {
        this.divCursor.style.left = `${status.x + this.cvsOffsetX}px`
        this.textInput.style.left = `${status.x + this.cvsOffsetX}px`
      }
      if (status.y !== undefined) {
        this.divCursor.style.top = `${status.y}px`
        this.textInput.style.top = `${status.y}px`
      }
      if (status.height !== undefined) {
        this.divCursor.style.height = `${status.height}px`
        this.textInput.style.height = `${status.height}px`
      }
      window.clearInterval(blinkTimer)
      if (status.visible === true) {
        blinkTimer = window.setInterval(() => {
          setCursorVisibility(!cursorVisible)
        }, 540)
      }
      if (status.visible !== undefined) {
        setCursorVisibility(status.visible)
      }
    }
  })()

  /**
   * 编辑器构造函数
   * @param container 编辑器容器 DOM 元素
   * @param config 编辑器配置数据实例
   */
  constructor(container: HTMLDivElement, config: IEditorConfig) {
    super()
    this.config = { ...config }
    this.container = container
    this.toolbarContainer = document.createElement('div')
    this.docContentContainer = document.createElement('div')
    this.docContentContainer.classList.add('content-container')
    this.container.append(this.toolbarContainer)
    this.container.append(this.docContentContainer)
    this.initDOM()
    this.doc.setWidth(this.config.canvasWidth)
    this.selectionService = new SelectionService(this.doc)
    this.historyStackService = new HistoryStackService(this.doc)
    this.tableService = new TableService(this.doc, this.historyStackService)
    this.searchService = new SearchService(this.doc)
    this.contentService = new ContentService(this.doc, this.historyStackService, this.selectionService)
    this.paragraphService = new ParagraphService(this.doc, this.historyStackService, this.contentService)
    this.listService = new ListService(this.doc, this.historyStackService, this.contentService)
    this.quoteBlockService = new QuoteBlockService(
      this.doc,
      this.historyStackService,
      this.contentService,
      this.paragraphService,
    )
    this.codeBlockService = new CodeBlockService(
      this.doc,
      this.historyStackService,
      this.contentService,
      this.paragraphService,
    )
    this.tableController = new TableController(
      this,
      this.doc,
      this.contentService,
      this.selectionService,
      this.tableService,
    )
    this.contentController = new ContentController(this, this.doc, this.contentService)
    this.toolbarController =
      config.toolbar ??
      new ToolbarController(
        this,
        this.doc,
        this.contentService,
        this.searchService,
        this.selectionService,
        this.toolbarContainer,
        this.container,
        config.toolbarConfig ?? DEFAULT_TOOLBAR_CONFIG,
      )

    this.bindBasicEvents()
    this.bindReadEvents()
    if (this.config.canEdit) {
      this.bindEditEvents()
      this.bindToolbarEvents()
    }
  }

  /**
   * 通过 delta 初始化文档内容
   * @param delta change 数组
   */
  public readFromChanges(delta: Delta) {
    this.historyStackService.setInitDelta(delta)
    this.doc.readFromChanges(delta)
    this.startDrawing()
  }

  public closeDocument() {
    this.doc.destroy()
    this.selectionService.reset()
    this.searchService.reset()
    this.contentService.reset()
    this.historyStackService.reset()
    this.tableService.reset()
    this.paragraphService.reset()
    this.quoteBlockService.reset()
    this.codeBlockService.reset()
    this.listService.reset()
    this.tableController.reset()
    this.contentController.reset()
    this.scrollTo(0)
    this.textInput.blur()
  }

  /**
   * 在指定位置用输入法开始插入内容
   * @param selection 要开始输入法输入的选区范围
   * @param attr 输入的格式
   */
  public startComposition() {
    this.composing = true
    this.contentService.startComposition(this.selectionService.getSelection())
  }

  /**
   * 更新输入法输入的内容
   * @param content 输入法中最新的输入内容
   * @param attr 输入的格式
   */
  public updateComposition(event: Event) {
    this.contentService.updateComposition(
      this.selectionService.getSelection()[0].start,
      (event as CompositionEvent).data,
      {},
    )
  }

  /**
   * 结束输入法输入
   * @param length 输入法输入内容的长度
   */
  public endComposition(event: { data: string }) {
    this.composing = false
    this.contentService.endComposition((event as CompositionEvent).data)
    this.textInput.value = ''
  }

  /**
   * 清除文档内容
   */
  public clearData() {
    // TODO
  }

  /**
   * 滚动可视区域到指定位置
   */
  public scrollTo(posY: number) {
    this.heightPlaceholderContainer.scrollTop = posY
  }

  /**
   * 搜索指定字符串
   */
  public search(keywords: string, caseSensitive: boolean) {
    const res = this.searchService.search(keywords)
    if (res.length > 0) {
      const targetResult = res[0]
      this.scrollToViewPort(targetResult.rects[0].y)
    }
  }

  /**
   * 清除搜索
   */
  public clearSearch() {
    this.searchService.clearSearch()
  }

  /**
   * 选中下一个搜索结果
   */
  public nextSearchResult() {
    const nextRes = this.searchService.nextSearchResult()
    if (nextRes !== null) {
      this.scrollToViewPort(nextRes.res.rects[0].y)
    }
  }

  /**
   * 选中上一个搜索结果
   */
  public prevSearchResult() {
    const prevRes = this.searchService.prevSearchResult()
    if (prevRes !== null) {
      this.scrollToViewPort(prevRes.res.rects[0].y)
    }
  }

  /**
   * 替换
   */
  public replace(replaceWords: string, all = false) {
    const diff = this.searchService.replace(replaceWords, all)
    this.historyStackService.pushDiff(diff)
    const newResult = this.searchService.getSearchResult()
    const newIndex = this.searchService.searchResultCurrentIndex
    if (newIndex !== undefined && newResult.length > 0) {
      this.scrollToViewPort(newResult[newIndex].rects[0].y)
    }
  }

  /**
   * undo
   */
  public undo() {
    const undoDelta = this.historyStackService.undo()
    if (undoDelta) {
      this.contentService.applyChanges(undoDelta)
    }
  }

  /**
   * redo
   */
  public redo() {
    const redoDelta = this.historyStackService.redo()
    if (redoDelta) {
      this.contentService.applyChanges(redoDelta)
    }
  }

  /**
   * 开始绘制任务
   * @param {boolean} fast 是否为快速绘制
   */
  public startDrawing(fast = false) {
    if (this.needRender === RenderType.NoRender) {
      requestAnimationFrame(this.render)
      this.needRender = RenderType.FastRender
    }
    if (!fast) {
      this.needRender = RenderType.Render
    }
  }

  // 用户操作工具栏控件设置格式
  public setFormat(data: { [key: string]: any }) {
    console.log('format ', data)
    this.contentService.format(data, this.selectionService.getSelection())
  }

  public clearFormat() {
    console.log('clear format')
    this.contentService.clearFormat(this.selectionService.getSelection())
  }

  public setIndent(direction: boolean) {
    this.contentService.setIndent(direction, this.selectionService.getSelection())
  }

  public setQuoteBlock() {
    this.quoteBlockService.setQuoteBlock(this.selectionService.getSelection())
    this.startDrawing()
  }

  public setCodeBlock() {
    this.codeBlockService.setCodeBlock(this.selectionService.getSelection())
    this.startDrawing()
  }

  public setLink(url: string, text: string) {
    console.log('set link', url, text)
  }

  public onSetList(listType: EnumListType) {
    this.listService.setList(listType, this.selectionService.getSelection())
    this.startDrawing()
  }

  public onSetParagraph() {
    this.paragraphService.setParagraph(this.selectionService.getSelection())
    this.startDrawing()
  }

  public insertImage(url: string, originalWidth: number, originalHeight: number) {
    let width = originalWidth
    let height = originalHeight
    if (originalWidth > this.config.canvasWidth) {
      width = this.config.canvasWidth
      height = (originalHeight * this.config.canvasWidth) / originalWidth
    }
    this.selectionService.restoreSelection()
    this.contentController.insertImage(url, width, height)
  }

  public insertTable(rowCount: number, colCount: number) {
    this.tableController.insertTable(this.selectionService.getSelection(), rowCount, colCount)
  }

  /**
   * 把指定的绝对坐标滚动到可视区域
   */
  private scrollToViewPort(posY: number) {
    if (posY > this.scrollTop + this.config.containerHeight || posY < this.scrollTop) {
      const targetScrollTop = Math.floor(posY - this.config.containerHeight / 2)
      this.scrollTo(targetScrollTop)
    }
  }

  private bindBasicEvents() {
    this.heightPlaceholder.addEventListener('mousedown', this.onMouseDown, true)
    document.addEventListener('mousemove', this.onMouseMove, true)
    document.addEventListener('click', this.onClick, true)
    this.heightPlaceholderContainer.addEventListener('scroll', this.onEditorScroll)
  }

  /**
   * 绑定阅读文档所需的相关事件
   */
  private bindReadEvents() {
    this.doc.em.addListener(DocumentEvent.DOCUMENT_FIRST_RENDERED, () => {
      this.emit(EditorEvent.DOCUMENT_FIRST_RENDERED)
    })
    this.doc.em.addListener(DocumentEvent.DOCUMENT_FIRST_LAYOUT, () => {
      this.emit(EditorEvent.DOCUMENT_FIRST_LAYOUT)
    })
    this.doc.em.addListener(DocumentEvent.DOCUMENT_CHANGE_SIZE, this.setEditorHeight)
    this.doc.em.addListener(DocumentEvent.DOCUMENT_CHANGE_CONTENT, this.onDocumentContentChange)

    this.doc.em.addListener(DocumentEvent.DOCUMENT_NEED_DRAW, this.onDocumentNeedDraw)
    this.searchService.addListener(SearchServiceEvent.SEARCH_NEED_DRAW, this.onSearchNeedDraw)
    this.searchService.addListener(SearchServiceEvent.SEARCH_RESULT_CHANGE, this.onSearchResultChange)

    this.doc.em.addListener('OPEN_LINK', this.openLink)

    this.selectionService.addListener(SelectionServiceEvent.CHANGE_SELECTION, this.onSelectionChange)
  }

  /**
   * 绑定编辑文档所需的相关事件
   */
  private bindEditEvents() {
    this.doc.em.addListener(DocumentEvent.DOCUMENT_AFTER_LAYOUT, this.updateCursorStatus)
    // this.historyStackService.addListener(
    //   HistoryStackServiceEvent.HISTORY_STACK_CHANGE,
    //   this.toolbar.setRedoUndoStatus.bind(this.toolbar),
    // )
    this.textInput.addEventListener('keydown', (event) => {
      if (event.key === 'Backspace') {
        this.contentService.delete(true)
      } else if (event.key === 'ArrowUp') {
        this.selectionService.cursorMoveUp()
      } else if (event.key === 'ArrowDown') {
        this.selectionService.cursorMoveDown()
      } else if (event.key === 'ArrowLeft') {
        if (event.metaKey || event.ctrlKey) {
          this.selectionService.cursorMoveToLineStart()
        } else {
          this.selectionService.cursorMoveLeft()
        }
      } else if (event.key === 'ArrowRight') {
        if (event.metaKey || event.ctrlKey) {
          this.selectionService.cursorMoveToLineEnd()
        } else {
          this.selectionService.cursorMoveRight()
        }
      }
    })
    this.textInput.addEventListener('input', () => {
      if (!this.composing) {
        const format = this.toolbarController.hasChange
          ? this.toolbarController.flattenFormat(this.toolbarController.currentFormat)
          : null
        this.contentService.input(this.textInput.value, format)
        this.textInput.value = ''
      }
    })
    this.textInput.addEventListener('blur', () => {
      if (this.composing) {
        this.endComposition({ data: this.textInput.value })
      }
      this.selectionService.clearSelection()
    })
    this.textInput.addEventListener('compositionstart', this.startComposition.bind(this))
    this.textInput.addEventListener('compositionupdate', this.updateComposition.bind(this))
    this.textInput.addEventListener('compositionend', this.endComposition.bind(this))
  }

  private bindToolbarEvents() {
    // this.toolbar.$on('setList', this.onSetList.bind(this))
    // this.toolbar.$on('setParagraph', this.onSetParagraph.bind(this))
  }

  /**
   * init some basic dom structure
   */
  private initDOM() {
    this.container.classList.add('fast-editor')
    this.container.style.width = `${this.config.containerWidth}px`
    this.container.style.height = `${this.config.containerHeight}px`

    this.cvsOffsetX = (this.config.containerWidth - this.config.canvasWidth) / 2
    this.docContentHeight =
      this.config.containerHeight - (this.config.toolbarConfig?.height ?? DEFAULT_TOOLBAR_CONFIG.height)
    this.docContentContainer.style.width = `${this.config.containerWidth}px`
    this.docContentContainer.style.height = `${this.docContentHeight}px`

    this.cvsDoc.classList.add('cvsDoc')
    this.cvsDoc.style.width = `${this.config.canvasWidth}px`
    this.cvsDoc.style.height = `${this.docContentHeight}px`
    this.cvsDoc.style.left = `${this.cvsOffsetX}px`

    this.cvsCover.classList.add('cvsCover')
    this.cvsCover.style.width = `${this.config.canvasWidth}px`
    this.cvsCover.style.height = `${this.docContentHeight}px`
    this.cvsCover.style.left = `${this.cvsOffsetX}px`

    this.domElementContainerWrapper.classList.add('domElementContainerWrapper')
    this.domElementContainerWrapper.style.width = `${this.config.canvasWidth}px`
    this.domElementContainerWrapper.style.height = `${this.docContentHeight}px`
    this.domElementContainerWrapper.style.left = `${this.cvsOffsetX}px`

    this.domElementContainer.classList.add('domElementContainer')
    this.domElementContainerWrapper.append(this.domElementContainer)

    const ratio = getPlatform().getPixelRatio(this.ctx)
    this.cvsDoc.width = this.config.canvasWidth * ratio
    this.cvsDoc.height = this.docContentHeight * ratio
    this.cvsCover.width = this.config.canvasWidth * ratio
    this.cvsCover.height = this.docContentHeight * ratio
    if (ratio !== 1) {
      this.ctx.scale(ratio, ratio)
    }

    this.heightPlaceholderContainer.classList.add('heightPlaceholderContainer')
    this.heightPlaceholder.classList.add('divHeightPlaceholder')

    this.divCursor = document.createElement('div')
    this.divCursor.classList.add('divCursor')
    this.divCursor.tabIndex = -1

    this.textInput = document.createElement('textarea')
    this.textInput.classList.add('textInput')
    this.textInput.tabIndex = -1
    this.textInput.autocomplete = 'off'
    this.textInput.autocapitalize = 'none'
    this.textInput.spellcheck = false

    this.heightPlaceholderContainer.appendChild(this.heightPlaceholder)
    this.heightPlaceholderContainer.appendChild(this.textInput)
    this.heightPlaceholderContainer.appendChild(this.divCursor)
    this.docContentContainer.appendChild(this.cvsDoc)
    this.docContentContainer.appendChild(this.domElementContainerWrapper)
    this.docContentContainer.appendChild(this.cvsCover)
    this.docContentContainer.appendChild(this.heightPlaceholderContainer)
  }

  /**
   * 绘制文档内容
   */
  private render = () => {
    if (this.needRender === RenderType.FastRender) {
      this.doc.fastDraw(this.ctx, this.scrollTop, this.docContentHeight)
    } else if (this.needRender === RenderType.Render) {
      this.doc.draw(this.ctx, this.scrollTop, this.docContentHeight)
    }
    this.needRender = RenderType.NoRender
  }

  private onEditorScroll = () => {
    this.scrollTop = this.heightPlaceholderContainer.scrollTop
    this.startDrawing(true)
  }

  private onMouseDown = (event: MouseEvent) => {
    document.addEventListener('mouseup', this.onMouseUp, true)
    const { x, y } = this.calOffsetDocPos(event)
    this.currentPointerScreenX = event.screenX
    this.currentPointerScreenY = event.screenY
    const docRect = {
      x: 0,
      y: 0,
      width: this.config.canvasWidth,
      height: this.config.containerHeight,
    }
    if (isPointInRectangle(x, y - this.scrollTop, docRect)) {
      this.selectionService.startMouseSelection(x, y)
    }
    this.doc.onPointerDown(x, y)
  }

  private onMouseMove = (event: MouseEvent) => {
    const { x, y } = this.calOffsetDocPos(event)
    const childrenStack = this.doc.getChildrenStackByPos(x, y)
    // 设置鼠标指针样式
    this.heightPlaceholderContainer.style.cursor = childrenStack[childrenStack.length - 1].getCursorType()
    this.currentPointerScreenX = event.screenX
    this.currentPointerScreenY = event.screenY
    this.selectionService.updateMouseSelection(x, y)
    const docRect = {
      x: 0,
      y: 0,
      width: this.config.canvasWidth,
      height: this.docContentHeight,
    }
    if (isPointInRectangle(x, y - this.scrollTop, docRect)) {
      if (!this.isPointerHoverDoc) {
        this.doc.onPointerEnter(x, y, childrenStack, 0)
        this.isPointerHoverDoc = true
      }
      this.doc.onPointerMove(x, y, childrenStack, 0)
    } else if (this.isPointerHoverDoc) {
      this.doc.onPointerLeave()
      this.isPointerHoverDoc = false
    }
  }

  private onMouseUp = (event: MouseEvent) => {
    document.removeEventListener('mouseup', this.onMouseUp, true)
    const { x, y } = this.calOffsetDocPos(event)
    this.currentPointerScreenX = event.screenX
    this.currentPointerScreenY = event.screenY
    this.selectionService.endMouseSelection(x, y)

    this.doc.onPointerUp(x, y)
    const selection = this.selectionService.getSelection()
    if (selection.length === 1 && compareDocPos(selection[0].start, selection[0].end) === 0) {
      const scrollPos = this.heightPlaceholderContainer.scrollTop
      const rects = this.selectionService.getSelectionRectangles()
      if (rects.length > 0) {
        this.textInput.focus()
        this.scrollTo(scrollPos)
      }
    } else if (selection.length > 0) {
      this.textInput.focus()
    }
  }

  private onClick = (event: MouseEvent) => {
    const { x, y } = this.calOffsetDocPos(event)
    this.currentPointerScreenX = event.screenX
    this.currentPointerScreenY = event.screenY
    const docRect = {
      x: 0,
      y: 0,
      width: this.config.canvasWidth,
      height: this.docContentHeight,
    }
    if (isPointInRectangle(x, y - this.scrollTop, docRect)) {
      this.doc.onPointerTap(x, y)
    }
  }

  private calOffsetDocPos = (event: MouseEvent): { x: number; y: number } => {
    const { clientX, clientY } = event
    const docContentBounding = this.docContentContainer.getBoundingClientRect()
    const x = clientX - docContentBounding.x - this.cvsOffsetX
    const y = clientY - docContentBounding.y + this.scrollTop
    return { x, y }
  }

  // 选区发生变化时要快速重绘
  private onSelectionChange = () => {
    this.startDrawing(true)
    this.updateCursorStatus()
  }

  // 内容发生变化正常重绘
  private onDocumentContentChange = () => {
    this.startDrawing()
  }

  // 文档需要快速重绘
  private onDocumentNeedDraw = () => {
    this.startDrawing(true)
  }

  // 因为搜索需要重绘
  private onSearchNeedDraw = () => {
    this.startDrawing(true)
  }

  private onSearchResultChange = (results: ISearchResult[], index: number) => {
    // 当搜索结果变化的时候要检测当前所有结果是不是在可见区域内
    this.startDrawing(true)
  }

  // 更新光标状态
  private updateCursorStatus = () => {
    const selection = this.selectionService.getSelection()
    if (selection.length === 1 && compareDocPos(selection[0].start, selection[0].end) === 0) {
      // 只有一个选区，而且选区的开始结束位置相同说明是光标模式
      const rect = this.selectionService.getSelectionRectangles()
      if (rect.length > 0) {
        this.changeCursorStatus({
          visible: true,
          x: rect[0].x,
          y: rect[0].y,
          height: rect[0].height,
        })
      }
    } else {
      this.changeCursorStatus({
        visible: false,
      })
    }
  }

  private openLink = (link: string) => {
    window.open(link)
  }
}
